import React, { useEffect, useReducer, useState } from 'react';
import api from '../api';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import Container from 'react-bootstrap/Container';
import ListGroup from 'react-bootstrap/ListGroup';
import Form from 'react-bootstrap/Form';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Button from 'react-bootstrap/Button';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { parsePriceInput } from '../utils/formInputs';
import { formatLayoutLabel } from '../utils/layoutInfo';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };
    case 'UPLOAD_REQUEST':
      return { ...state, loadingUpload: true, errorUpload: '' };
    case 'UPLOAD_SUCCESS':
      return {
        ...state,
        loadingUpload: false,
        errorUpload: '',
      };
    case 'UPLOAD_FAIL':
      return { ...state, loadingUpload: false, errorUpload: action.payload };

    default:
      return state;
  }
};
export default function ProductEditScreen() {
  const navigate = useNavigate();
  const params = useParams(); // safes the parameters (dynamic segments), defined in the URL, into the params variable: /product/:id
  const { id: productId } = params; // deconstracts the id out of the params variable and its name is set to productId

  const [{ loading, error, loadingUpdate, loadingUpload }, dispatch] =
    useReducer(reducer, {
      loading: true,
      error: '',
    });

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [unitGross, setUnitGross] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [images, setImages] = useState([]);
  const [category, setCategory] = useState('');
  const [layouts, setLayouts] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState('');
  const [keycaps, setKeycaps] = useState([]);
  const [selectedKeycap, setSelectedKeycap] = useState('');
  const [germanDescription, setGermanDescription] = useState('');
  const [englishDescription, setEnglishDescription] = useState('');
  const [vatRate, setVatRate] = useState(0);
  const [visibility, setVisibility] = useState('');
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [allowedUserInput, setAllowedUserInput] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [usedLayoutIds, setUsedLayoutIds] = useState(new Set());

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });

      try {
        const [layoutsResponse, keycapsResponse, productResponse] =
          await Promise.all([
            api.get(`/api/layouts/product-reference`),
            api.get(`/api/keycaps/product-reference`),
            api.get(`/api/products/${productId}`),
          ]);

        const layoutsData = layoutsResponse.data;
        const keycapsData = keycapsResponse.data;
        const productData = productResponse.data;

        setLayouts(layoutsData);
        setKeycaps(keycapsData);

        setName(productData.name);
        setSlug(productData.slug);

        const displayFormattedValue = new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          currencyDisplay: 'symbol',
        }).format(parseFloat(productData.unitGross));

        setUnitGross(displayFormattedValue);

        setThumbnail(productData.thumbnail);
        setImages(productData.images);
        setCategory(productData.category);
        setGermanDescription(productData.germanDescription);
        setEnglishDescription(productData.englishDescription);
        setVatRate(productData.vatRate);
        setSelectedLayout(productData.layout);
        setSelectedKeycap(productData.keycap);
        setVisibility(productData.visibility ?? 'public');
        setAllowedUsers(
          Array.isArray(productData.allowedUsers)
            ? productData.allowedUsers.map((s) => String(s).toUpperCase())
            : []
        );
        setIsActive(productData.isActive);

        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };
    fetchData(); // fetches the product information based on the product id, from the backend
  }, [productId]);

  useEffect(() => {
    const fetchUsedLayouts = async () => {
      try {
        const { data: products } = await api.get('/api/products/admin/all');

        const usedIds = new Set();
        products.forEach((product) => {
          // Exclude the current product
          if (product._id !== productId && product.layout) {
            usedIds.add(product.layout);
          }
        });

        setUsedLayoutIds(usedIds);
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };
    fetchUsedLayouts();
  }, [productId]); // Fetch used layout IDs from all products other than the current one

  useEffect(() => {
    if (category !== 'Custom Keycap Set') {
      setSelectedLayout('');
    }

    if (category !== 'Custom Keycap') {
      setSelectedKeycap('');
    }
  }, [category]);

  const submitHandler = async (e) => {
    e.preventDefault(); // used, so the page doesn't refresh, when the submit button is clicked
    const finalUnitGross = parsePriceInput(unitGross); // the German format (1.234,56) as a number
    if (isNaN(finalUnitGross)) {
      toast.error('Ungültiger Preiswert');
      return; // checked before UPDATE_REQUEST: a return after it left the form behind the loading overlay (fixed Oct 2026)
    }

    try {
      dispatch({ type: 'UPDATE_REQUEST' });

      const requestBody = {
        _id: productId,
        name,
        slug,
        unitGross: finalUnitGross,
        thumbnail,
        images,
        category,
        germanDescription,
        englishDescription,
        vatRate,
        visibility,
        allowedUsers,
        isActive,
      };

      if (selectedLayout) {
        requestBody.selectedLayout = selectedLayout;
      } // don't include the selectedLayout in the put request, if it is a falsy value (empty string for example)

      if (selectedKeycap) {
        requestBody.selectedKeycap = selectedKeycap;
      } // don't include the selectedKeycap in the put request, if it is a falsy value (empty string for example)

      await api.put(`/api/products/${productId}`, requestBody);
      dispatch({
        type: 'UPDATE_SUCCESS',
      });
      toast.success('Produkt-Einstellungen gespeichert');
      navigate('/admin/products');
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPDATE_FAIL' });
    }
  }; // a put request to the backend, that ultimalty updates the product information based on the data from the Form

  const uploadFileHandler = async (e, forImages) => {
    const file = e.target.files[0]; // gets the file from the event
    const bodyFormData = new FormData(); // FormData is a built-in JavaScript class that provides a way to construct and manage HTML form data. It is commonly used in web applications to prepare data for sending via HTTP requests
    bodyFormData.append('image', file); // adds the image to the form data, making it available to be sent in an HTTP request (file data will be included and can be processed on the server side)
    try {
      dispatch({ type: 'UPLOAD_REQUEST' });
      const uploadResponse = await api.post(
        '/api/aws/custom-keycaps-products',
        bodyFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data', // indicates that the request body contains form data in the 'multipart/form-data' format, which is typically used for file uploads
          },
        }
      ); // request to post the bodyFormData to the /api/upload endpoint

      const { url } = uploadResponse.data;

      dispatch({ type: 'UPLOAD_SUCCESS' });
      if (forImages) {
        setImages([...images, url]);
      } else {
        setThumbnail(url);
      }
      toast.success('Bild hochgeladen');
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPLOAD_FAIL', payload: getError(err) });
    }
  };

  const deleteFileHandler = async (fileName, f) => {
    setImages(images.filter((x) => x !== fileName));
    toast.success('Bild gelöscht');
  };

  const deleteThumbnailHandler = async () => {
    setThumbnail('');
    toast.success('Bild gelöscht');
  };

  return (
    <Container>
      <Container className="small-container">
        <title>Produkt bearbeiten {productId}</title>

        <h1 className="screen-heading">Produkt bearbeiten</h1>

        <p className="product-id-heading">(ID: {productId})</p>

        {loading || loadingUpload || loadingUpdate ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <Form onSubmit={submitHandler}>
            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="slug">
              <Form.Label>Slug</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="unitGross">
              <Form.Label className="d-flex">
                Einzelpreis (Brutto)
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bitte geben Sie den Preis im richtigen Format ein:{' '}
                        <br />
                        <strong>1.234,56 (Deutsches Format)</strong>
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={unitGross}
                onChange={(e) => setUnitGross(e.target.value)}
                required
              />
            </Form.Group>
            <div
              className={'disabled-input'}
              title={
                'Die Rechnung verwendet momentan noch keine dynamischen Steuersätze, weshalb diese Funktion deaktiviert ist'
              }
            >
              <Form.Group className="mb-3" controlId="category">
                <Form.Label>MwSt. Steuersatz</Form.Label>
                <Form.Select
                  className="mobile-device-compatible"
                  value={vatRate}
                  onChange={(e) => setVatRate(e.target.value)}
                  required
                >
                  {/*
                    <option className="option-tag" value="">
                      Wähle einen Steuersatz aus
                    </option>
                    <option className="option-tag" value={19}>
                      19%
                    </option>
                    <option className="option-tag" value={16}>
                      16%
                    </option>
                    <option className="option-tag" value={7}>
                      7%
                    </option>
                    <option className="option-tag" value={5}>
                      5%
                    </option>
                  */}
                  <option className="option-tag" value={0}>
                    0%
                  </option>
                </Form.Select>
              </Form.Group>
            </div>
            <Form.Group className="mb-3" controlId="imageFile">
              <Form.Label>Thumbnail hochladen</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type="file"
                onChange={uploadFileHandler}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="additionalImage">
              {thumbnail.length === 0 && (
                <MessageBox>Keine Thumbnail hochgeladen</MessageBox>
              )}
            </Form.Group>
            <ListGroup className="mb-3" variant="flush">
              {thumbnail ? (
                <ListGroup.Item className="url-list">
                  <Button
                    variant="dark"
                    size="sm"
                    type="button"
                    onClick={() => deleteThumbnailHandler()}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-trash3"
                      viewBox="0 0 16 16"
                    >
                      <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                    </svg>
                  </Button>
                  <div className="image-url">{thumbnail}</div>
                </ListGroup.Item>
              ) : (
                ''
              )}
            </ListGroup>
            <Form.Group className="mb-3" controlId="additionalImageFile">
              <Form.Label>Weitere Bilder hochladen</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type="file"
                onChange={(e) => uploadFileHandler(e, true)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="additionalImage">
              {images.length === 0 && (
                <MessageBox>Keine weiteren Bilder hochgeladen</MessageBox>
              )}
            </Form.Group>
            <ListGroup className="mb-3" variant="flush">
              {images.map((x) => (
                <ListGroup.Item className="url-list" key={x}>
                  <Button
                    variant="dark"
                    size="sm"
                    type="button"
                    onClick={() => deleteFileHandler(x)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-trash3"
                      viewBox="0 0 16 16"
                    >
                      <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                    </svg>
                  </Button>
                  <div className="image-url">{x}</div>
                </ListGroup.Item>
              ))}
            </ListGroup>
            <Form.Group className="mb-3" controlId="category">
              <Form.Label>Kategorie</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
              >
                <option className="option-tag" value="">
                  Wähle eine Kategorie
                </option>
                <option className="option-tag" value="Custom Keycap Set">
                  Custom Keycap Set
                </option>
                <option className="option-tag" value="Keycap Set">
                  Keycap Set
                </option>
                <option className="option-tag" value="Custom Keycap">
                  Custom Keycap
                </option>
              </Form.Select>
            </Form.Group>
            {category === 'Custom Keycap Set' ? (
              <Form.Group className="mb-3" controlId="layout">
                <Form.Label>Layout</Form.Label>
                <Form.Select
                  className="mobile-device-compatible"
                  value={selectedLayout}
                  onChange={(e) => {
                    setSelectedLayout(e.target.value);
                    setName(e.target.options[e.target.selectedIndex].text);
                  }}
                  required
                >
                  <option className="option-tag" value="">
                    Wähle ein Layout
                  </option>

                  {layouts.map((layout) => {
                    // Disable the option if it’s used by another product,
                    // unless it’s the layout currently selected for this product.
                    const isUsed = usedLayoutIds.has(layout._id);
                    const disableOption =
                      isUsed && layout._id !== selectedLayout;

                    return (
                      <option
                        key={layout._id}
                        value={layout._id}
                        disabled={disableOption}
                      >
                        {formatLayoutLabel(layout)}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            ) : category === 'Custom Keycap' ? (
              <Form.Group className="mb-3" controlId="keycap">
                <Form.Label>Keycap</Form.Label>
                <Form.Select
                  className="mobile-device-compatible"
                  value={selectedKeycap}
                  onChange={(e) => setSelectedKeycap(e.target.value)}
                  required
                >
                  <option className="option-tag" value="">
                    Wähle eine Keycap
                  </option>
                  {keycaps.map((keycap) => (
                    <option key={keycap._id} value={keycap._id}>
                      {keycap.name} {keycap.color}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            ) : null}
            <Form.Group className="mb-3" controlId="germanDescription">
              <Form.Label>Deutsche Beschreibung</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                as="textarea"
                value={germanDescription}
                onChange={(e) => setGermanDescription(e.target.value)}
                required
                style={{
                  resize: 'none', // Disable manual resizing
                }}
                rows={10}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="englishDescription">
              <Form.Label>Englische Beschreibung</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                as="textarea"
                value={englishDescription}
                onChange={(e) => setEnglishDescription(e.target.value)}
                required
                style={{
                  resize: 'none', // Disable manual resizing
                }}
                rows={10}
              />
            </Form.Group>

            {/* Visibility Field */}
            <Form.Group className="mb-3" controlId="visibility">
              <Form.Label className="d-flex">
                Visibility
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bestimmt, ob dieses Produkt für alle Kunden sichtbar ist
                        (<strong>public</strong>) oder nur für eine Auswahl an
                        Kunden verfügbar ist (<strong>restricted</strong>). Bei
                        'restricted' muss mindestens ein erlaubter Benutzer in
                        <em> Allowed Users</em> angegeben werden.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              <Form.Select
                className="mobile-device-compatible"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                required
              >
                <option className="option-tag" value="public">
                  Public
                </option>
                <option className="option-tag" value="restricted">
                  Restricted
                </option>
              </Form.Select>
            </Form.Group>

            {/* Allowed Users Field */}
            <Form.Group className="mb-3" controlId="allowedUsers">
              <Form.Label className="d-flex">
                Allowed Users
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Liste von <strong>User IDs</strong> im Format
                        <code> XXX-XXX-XXX </code>. Nur relevant, wenn die
                        Sichtbarkeit auf <em>restricted</em> gesetzt ist.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              {/* Input field */}
              <Form.Control
                type="text"
                className="mobile-device-compatible"
                value={allowedUserInput}
                onChange={(e) =>
                  setAllowedUserInput(e.target.value.toUpperCase())
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const newId = e.target.value.trim().toUpperCase();
                    const USER_ID_RE = /^[A-Z]{3}-[A-Z]{3}-[A-Z]{3}$/;

                    if (!USER_ID_RE.test(newId)) {
                      toast.warning(
                        'User ID must match the format XXX-XXX-XXX (uppercase letters only).'
                      );
                      return;
                    }

                    if (!allowedUsers.includes(newId)) {
                      setAllowedUsers([...allowedUsers, newId]);
                    }
                    setAllowedUserInput(''); // reset input
                  }
                }}
                disabled={visibility !== 'restricted'}
              />

              {/* Tag list */}
              <div className="mt-2">
                {allowedUsers.map((id, index) => (
                  <span
                    key={index}
                    className="mx-1"
                    style={{
                      border: '1px solid black',
                      borderRadius: '5px',
                      color: '#fff',
                      backgroundColor: '#1a1a1a',
                      fontSize: '14px',
                      padding: '2px 6px',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    {id}
                    <Button
                      className="no-css ms-1"
                      variant="none"
                      style={{ color: '#fff', padding: 0, lineHeight: 1 }}
                      onClick={() =>
                        setAllowedUsers(
                          allowedUsers.filter((_, i) => i !== index)
                        )
                      }
                    >
                      &times;
                    </Button>
                  </span>
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3" controlId="isActive">
              <Form.Label className="d-flex">
                Active
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bestimmt, ob dieses Produkt dem Kunden zum Verkauf
                        steht. Ist dieses Produkt mit einem Layout verknüpft, so
                        bestimmt der 'isActive' Status, ob dieses Layout dem
                        Kunden im Customizer zur Auswahl steht. Zudem bestimmt
                        der Status, ob bereits erstellte Designs die auf diesem
                        Layout basieren, dem Kunden z.B. im
                        CommunityDesignsScreen und MyDesignsScreen zum Verkauf
                        zur Verfügung stehen.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isActive}
                onChange={(e) => setIsActive(e.target.value)}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>
            <div className="d-flex justify-content-center align-items-center mt-5">
              <Button
                variant="secondary"
                disabled={loadingUpdate}
                type="submit"
              >
                {/* disabled prop is set to loadingUpdate, because it should not be possible, to click the update button multiple times */}
                Speichern
              </Button>
            </div>
          </Form>
        )}
      </Container>
    </Container>
  );
}
